import { EDITOR_SESSION_SCHEMA_VERSION, IEditorSession } from '../model/types';

// One entry per version; other versions' sessions stay dormant until viewed.
export const EDITOR_STORAGE_PREFIX = 'routing-config-editor:v1:';

export const editorStorageKey = (versionId: number): string =>
  `${EDITOR_STORAGE_PREFIX}${versionId}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// localStorage is an untrusted boundary: a hand-edited or partly-corrupt entry
// must not reach diffPolicies/validateDraft (which dereference uid/rules) — so
// every policy and rule shape is checked, not just that the arrays exist.
const isEditorPolicyArray = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.every(
    policy =>
      isRecord(policy) &&
      typeof policy.uid === 'string' &&
      Array.isArray(policy.rules) &&
      policy.rules.every(rule => isRecord(rule) && typeof rule.uid === 'string'),
  );

const isEditorSession = (value: unknown, versionId: number): value is IEditorSession => {
  if (!isRecord(value)) return false;
  const candidate = value as Partial<IEditorSession>;

  return (
    candidate.schemaVersion === EDITOR_SESSION_SCHEMA_VERSION &&
    candidate.versionId === versionId &&
    typeof candidate.baseUpdatedAt === 'string' &&
    (candidate.savedDraftId === null || typeof candidate.savedDraftId === 'number') &&
    isEditorPolicyArray(candidate.original) &&
    isEditorPolicyArray(candidate.draft)
  );
};

// Corrupt, foreign or unreadable entries count as "no session" — never as an error.
export const readEditorSession = (versionId: number): IEditorSession | null => {
  try {
    const raw = localStorage.getItem(editorStorageKey(versionId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);

    return isEditorSession(parsed, versionId) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeEditorSession = (session: IEditorSession): void => {
  try {
    localStorage.setItem(editorStorageKey(session.versionId), JSON.stringify(session));
  } catch {
    // Ignore storage errors (private mode, quota).
  }
};

export const removeEditorSession = (versionId: number): void => {
  try {
    localStorage.removeItem(editorStorageKey(versionId));
  } catch {
    // Ignore storage errors.
  }
};
